/**
 * Development-only authentication, backed by the server's own dev bypass.
 *
 * ⚠️ IT IS NOT A MOCK, AND THAT IS THE POINT. It asks the REAL
 * `GET /api/auth/status` and returns whatever the REAL server says — which,
 * when the API runs with `ENABLE_AUTH=false`, is the dev-bypass user
 * `getDevBypassUser()` resolves from `DEV_BYPASS_USER_ID` / `DEV_BYPASS_USER_UPN`,
 * complete with their real roles from the database. So the client and the
 * server agree on who is signed in, down to the user id, and every
 * permission-gated screen behaves as it will in production for that person.
 * A fabricated user would agree with nothing.
 *
 * ⚠️ WHY IT EXISTS. The Okta flow cannot complete on a machine with no Apple
 * code-signing identity: authentication itself succeeds, and then
 * `react-native-encrypted-storage` fails to write the token to the Keychain,
 * because an ad-hoc-signed app has no `application-identifier` entitlement.
 * The symptom is "Authentication failed. Please try again." with a successful
 * token exchange in the log just above it. That blocks every local test that
 * needs a session — including push, where the permission prompt only appears
 * after sign-in.
 *
 * ⚠️ THREE GATES, AND THE DEFAULT AT EACH IS "NO".
 *
 *   1. `__DEV__` — it cannot exist in a release bundle at all.
 *   2. `REACT_APP_DEV_AUTH_BYPASS` must be exactly `true`.
 *   3. The SERVER must independently agree: it only ever returns a user
 *      because `ENABLE_AUTH=false` there. A deployed API answers 401 and this
 *      service reports anonymous, so a client built with the flag on cannot
 *      talk its way into a real environment.
 *
 * The third gate is the one that matters. The first two are a client saying
 * "I would like to be signed in"; only the server can grant it.
 *
 * ⚠️ IT TOUCHES NO KEYCHAIN AND HOLDS NO TOKEN. `getToken` answers `null`, so
 * `api.ts` sends no `Authorization` header — which is exactly right, because
 * the server is not checking one. Nothing is persisted, so there is no stale
 * session to clear and `logout` is genuinely a no-op.
 *
 * @see server/middleware/auth.ts — `getDevBypassUser`, the other half
 * @see src/services/auth/authServiceFactory.ts — the gate
 */

import { getResolvedApiBaseUrl } from '../api';
import { fetchAuthStatusHttp } from './fetchAuthStatus';
import { mapAuthStatusUser } from './mapAuthStatusUser';

import type { AuthStatusResult, IAuthService, User } from '../../types/auth';

const statusUrl = (): string => `${getResolvedApiBaseUrl()}/auth/status`;

const readStatus = async (): Promise<AuthStatusResult> => {
  try {
    const http = await fetchAuthStatusHttp(statusUrl(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!http.ok) return { kind: 'anonymous' };

    const body = http.body as { isAuthenticated?: boolean; user?: unknown };
    if (!body?.isAuthenticated) return { kind: 'anonymous' };

    const user = mapAuthStatusUser(body.user);
    // A body that claims a session but carries no usable user is a server
    // that is not actually bypassing auth; anonymous is the honest answer.
    return user ? { kind: 'user', user } : { kind: 'anonymous' };
  } catch {
    // Offline, or no API running. Not an error state for the caller: the
    // login screen is the correct place to be.
    return { kind: 'anonymous' };
  }
};

export const devAuthService: IAuthService = {
  async login(): Promise<User> {
    const status = await readStatus();
    if (status.kind !== 'user') {
      // Says exactly which of the two halves is missing — this message is
      // the whole debugging experience for somebody who turned the flag on
      // and pointed it at the wrong API.
      throw new Error(
        'Dev auth bypass: the API did not return a session. Is it running ' +
          'with ENABLE_AUTH=false?',
      );
    }
    return status.user;
  },

  async logout(): Promise<void> {
    // Nothing is stored, so there is nothing to clear. Deliberately not a
    // call to the server's logout: there is no session there to end either.
  },

  async getCurrentUser(): Promise<User | null> {
    const status = await readStatus();
    return status.kind === 'user' ? status.user : null;
  },

  async getAuthStatus(): Promise<AuthStatusResult> {
    return readStatus();
  },

  async setToken(): Promise<void> {
    // No token is held; see the module header.
  },

  /**
   * ⚠️ NULL, AND IT MUST STILL EXIST. The module header has always said
   * "`getToken` answers `null`" — it simply was not written, and `api.ts`
   * calls it on every single request to build the `Authorization` header. The
   * absence cost an afternoon of #1642 Android debugging: every API call from
   * a dev-bypass session threw `authService.getToken is not a function`, which
   * `api.ts` reported as a network error, which sent the hunt to the server,
   * the emulator's networking and the cleartext policy in turn.
   *
   * Null is the right answer, not a fabricated token: the server is running
   * with `ENABLE_AUTH=false` and is not checking one.
   */
  async getToken(): Promise<string | null> {
    return null;
  },
};
